import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { classNames } from "../../helpers";
import * as dashboardActions from "../../redux/reducers/dashboardReducer";
import { clearLoadingData, setGlobalLoading } from "../../redux/reducers/loadingReducer";
import { clearSchoolData } from "../../redux/reducers/schoolReducer";
import * as userActions from "../../redux/reducers/userReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForUsers } from "../../utils/confighelpers";
import MaskIcon, { MEMBER_ICONS } from "../tailwind/MaskIcon";
import TwInitialsAvatar from "../tailwind/TwInitialsAvatar";

/**
 * Profile pill for the onboarding header (/start/*), the same pill as in the app bar after onboarding
 * (avatar + "Sanne d." + chevron). The menu shows who is signed in and a Log out option.
 * Logging out does the same as TwMainLayout's logout: logoutUser, clear the session, back to "/".
 */
export default function OnboardingUserMenu() {
  const dispatch = useDispatch();
  const userData = useSelector(userState)?.userData;
  if (!userData?._id) return null;

  const parts = (userData.fullName || "").trim().split(" ").filter(Boolean);
  const shortName = parts.length > 1 ? `${parts[0]} ${parts[1].substring(0, 1)}.` : parts[0] || "";

  const logout = async () => {
    try {
      dispatch(setGlobalLoading(true));
      await axiosInstance({ method: "put", url: `${getUrlForUsers()}logoutUser`, data: {} });
    } catch (e) {
      // signed out locally anyway
    } finally {
      try {
        localStorage.removeItem("userToken");
        localStorage.removeItem("persist:root");
        localStorage.removeItem("fcOnboardingPlan");
      } catch (e) {
        // storage unavailable
      }
      dispatch(userActions.clearUserData());
      dispatch(clearSchoolData());
      dispatch(dashboardActions.clearDashboardData());
      dispatch(clearLoadingData());
      window.location.href = "/";
    }
  };

  return (
    <Menu as="div" className="relative shrink-0">
      <MenuButton className="-m-1.5 flex items-center rounded-full bg-zinc-300 p-1.5">
        <span className="sr-only">Open user menu</span>
        {userData.profilePic ? (
          <img className="size-8 lg:size-9 rounded-full bg-gray-50" alt="" src={userData.profilePic} />
        ) : (
          <TwInitialsAvatar name={shortName} />
        )}
        <span className="hidden lg:flex lg:items-center">
          <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
            {shortName}
          </span>
          <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
        </span>
      </MenuButton>
      <MenuItems
        transition
        className="absolute right-0 z-40 mt-2.5 w-60 origin-top-right rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
      >
        <div className="border-b border-gray-100 px-3 pb-2.5 pt-1">
          <p className="truncate text-sm font-extrabold text-gray-900">{userData.fullName}</p>
          {userData.email && <p className="truncate text-xs text-gray-500">{userData.email}</p>}
        </div>
        <MenuItem>
          <button
            type="button"
            onClick={logout}
            className={classNames(
              "mt-1 flex w-full items-center gap-2.5 whitespace-nowrap px-3 py-1.5 text-start text-sm/6 font-semibold text-[#C53030]",
              "hover:bg-gray-50 focus:bg-gray-50 focus:outline-none data-[focus]:bg-gray-50"
            )}
          >
            <MaskIcon src={MEMBER_ICONS.logout} className="size-5 text-[#C53030]" />
            Log out
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}
